import React, { useRef } from "react";
import { gsap, prefersReducedMotion, useGSAP } from "./gsap";

interface CountUpProps {
  to: number;
  /** Seconds for the run from the previous value. */
  duration?: number;
  format?: (value: number) => string;
  className?: string;
}

const defaultFormat = (value: number) => value.toLocaleString("en-US");

/**
 * CountUp after React Bits (https://reactbits.dev, ../REACT_BITS_LICENSE.md),
 * on GSAP instead of motion so the admin keeps one animation library. The
 * number runs up from zero on mount, then from its last value to a new one —
 * a refetch shows how much it moved. Digits are tabular, so the text around
 * it does not shake while it runs.
 */
const CountUp: React.FC<CountUpProps> = ({
  to,
  duration = 0.9,
  format = defaultFormat,
  className,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const shown = useRef({ value: 0 });

  // The span has no React children: the tween owns its text, and React
  // would otherwise keep writing into a text node the tween replaced.
  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      const draw = () => {
        el.textContent = format(Math.round(shown.current.value));
      };
      if (prefersReducedMotion() || shown.current.value === to) {
        shown.current.value = to;
        draw();
        return;
      }
      draw();
      gsap.to(shown.current, {
        value: to,
        duration,
        ease: "power2.out",
        // A newer value takes over a run still under way.
        overwrite: true,
        onUpdate: draw,
        onComplete: draw,
      });
    },
    { dependencies: [to] },
  );

  return (
    <span
      ref={ref}
      className={className}
      style={{ fontVariantNumeric: "tabular-nums" }}
    />
  );
};

export default CountUp;
